import React, { useEffect, useState } from 'react';
import {
  collection, getDocs, query, where,
  doc, updateDoc, serverTimestamp
} from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { writeAuditLog } from '../utils/auditLog';

const TABS    = ['all', 'pending', 'out', 'delivered', 'missed'];
const ST_COLOR = {
  pending:   { bg: '#FEF3C7', color: '#D97706' },
  out:       { bg: '#DBEAFE', color: '#1768C4' },
  delivered: { bg: '#DCFCE7', color: '#16A34A' },
  missed:    { bg: '#FEE2E2', color: '#DC2626' },
};

export default function DeliveriesPage() {
  const { pharmacy, role, user }       = useAuth();
  const [patients, setPatients]  = useState([]);
  const [tab, setTab]            = useState('all');
  const [loading, setLoading]    = useState(true);
  const [editNote, setEditNote]  = useState(null); // { id, note }

  const canWrite = ['admin', 'pharmacist', 'technician', 'superadmin'].includes(role);

  const load = async () => {
    if (!pharmacy) return;
    setLoading(true);
    const snap = await getDocs(
      query(collection(db, 'pharmacies', pharmacy.id, 'patients'),
        where('deliveryPatient', '==', true))
    );
    setPatients(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    setLoading(false);
  };

  useEffect(() => { load(); }, [pharmacy]);

  const setStatus = async (p, status) => {
    await updateDoc(doc(db, 'pharmacies', pharmacy.id, 'patients', p.id), {
      deliveryStatus: status, updatedAt: serverTimestamp(),
      updatedBy: user.uid,
      updatedByName: user.email,
      updatedByRole: role,
    });
    await writeAuditLog(pharmacy.id, user, {
      action: 'Delivery status changed',
      entityType: 'patient',
      entityId: p.id,
      entityName: p.name,
      previousValue: p.deliveryStatus || '',
      newValue: status,
      actorRole: role || '',
      source: 'client',
    });
    load();
  };

  const saveNote = async () => {
    if (!editNote) return;
    await updateDoc(doc(db, 'pharmacies', pharmacy.id, 'patients', editNote.id), {
      deliveryNote: editNote.note.trim(), updatedAt: serverTimestamp(),
      updatedBy: user.uid,
      updatedByName: user.email,
      updatedByRole: role,
    });
    await writeAuditLog(pharmacy.id, user, {
      action: 'Delivery note changed',
      entityType: 'patient',
      entityId: editNote.id,
      entityName: editNote.name || editNote.id,
      previousValue: editNote.deliveryNote || '',
      newValue: editNote.note.trim(),
      actorRole: role || '',
      source: 'client',
    });
    setEditNote(null);
    load();
  };

  const displayed = tab === 'all'
    ? patients
    : patients.filter(p => (p.deliveryStatus || 'pending') === tab);

  // Tab counts for badges
  const countFor = (s) => s === 'all'
    ? patients.length
    : patients.filter(p => (p.deliveryStatus || 'pending') === s).length;

  return (
    <div style={{ padding: '28px 32px', fontFamily: 'Inter, system-ui, sans-serif' }}>

      {/* Header */}
      <h1 style={{ margin: '0 0 4px', fontSize: 24, fontWeight: 800, color: '#0F172A' }}>Deliveries</h1>
      <p style={{ margin: '0 0 20px', fontSize: 13, color: '#94A3B8' }}>
        {patients.length} patient{patients.length !== 1 ? 's' : ''} on delivery route
      </p>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 0, marginBottom: 20,
        borderBottom: '1px solid #E2E8F0' }}>
        {TABS.map(s => {
          const cnt = countFor(s);
          return (
            <button key={s} onClick={() => setTab(s)} style={{
              padding: '9px 18px', background: 'none', border: 'none',
              borderBottom: tab === s ? '2px solid #0F172A' : '2px solid transparent',
              color: tab === s ? '#0F172A' : '#94A3B8',
              fontWeight: tab === s ? 700 : 400, fontSize: 13,
              cursor: 'pointer', textTransform: 'capitalize',
              marginBottom: -1, display: 'flex', alignItems: 'center', gap: 6,
            }}>
              {s}
              {cnt > 0 && s !== 'all' && (
                <span style={{
                  fontSize: 10, fontWeight: 700,
                  background: tab === s ? '#0F172A' : '#E2E8F0',
                  color: tab === s ? '#fff' : '#64748B',
                  borderRadius: 99, padding: '1px 6px',
                }}>{cnt}</span>
              )}
            </button>
          );
        })}
      </div>

      {/* Note edit modal */}
      {editNote && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100,
        }}>
          <div style={{
            background: '#fff', borderRadius: 12, padding: 28, width: 360,
            boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
          }}>
            <h3 style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 700 }}>Delivery Note</h3>
            <textarea value={editNote.note}
              onChange={e => setEditNote({ ...editNote, note: e.target.value })}
              rows={3}
              placeholder="Enter delivery note…"
              style={{ width: '100%', padding: '9px 12px', border: '1px solid #D1D5DB',
                borderRadius: 8, fontSize: 14, boxSizing: 'border-box',
                resize: 'vertical', fontFamily: 'inherit' }} />
            <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
              <button onClick={saveNote} style={{
                flex: 1, padding: 10, background: '#0F172A', color: '#fff',
                border: 'none', borderRadius: 8, fontWeight: 600, cursor: 'pointer',
              }}>Save</button>
              <button onClick={() => setEditNote(null)} style={{
                flex: 1, padding: 10, background: '#F1F5F9', color: '#0F172A',
                border: 'none', borderRadius: 8, fontWeight: 600, cursor: 'pointer',
              }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Table */}
      <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #E2E8F0', overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#94A3B8' }}>Loading…</div>
        ) : displayed.length === 0 ? (
          <div style={{ padding: 48, textAlign: 'center', color: '#94A3B8' }}>
            No delivery patients in this category.
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#F8FAFC' }}>
                {['Patient', 'Cycle', 'Next Due', 'Status', 'Delivery Note', 'Actions'].map(h => (
                  <th key={h} style={{
                    padding: '10px 16px', textAlign: 'left', fontSize: 11,
                    fontWeight: 600, color: '#94A3B8', textTransform: 'uppercase',
                    letterSpacing: '0.05em', borderBottom: '1px solid #F1F5F9',
                  }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {displayed.map(p => {
                const st = p.deliveryStatus || 'pending';
                const sc = ST_COLOR[st] || ST_COLOR.pending;
                const nd = p.nextDue?.toDate ? p.nextDue.toDate() : null;
                return (
                  <tr key={p.id} style={{ borderBottom: '1px solid #F8FAFC' }}>

                    <td style={{ padding: '12px 16px', fontWeight: 600, color: '#0F172A' }}>
                      {p.name}
                    </td>

                    <td style={{ padding: '12px 16px', color: '#64748B' }}>
                      {p.cycleLength || 28}d
                    </td>

                    <td style={{ padding: '12px 16px', color: '#64748B' }}>
                      {nd ? nd.toISOString().slice(0, 10) : '—'}
                    </td>

                    <td style={{ padding: '12px 16px' }}>
                      <span style={{
                        padding: '3px 10px', borderRadius: 20, fontSize: 11,
                        fontWeight: 600, background: sc.bg, color: sc.color,
                        textTransform: 'capitalize',
                      }}>{st}</span>
                    </td>

                    {/* Delivery note — click pencil to edit */}
                    <td style={{ padding: '12px 16px', color: '#64748B', fontSize: 12, maxWidth: 220 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {p.deliveryNote || <span style={{ color: '#CBD5E1' }}>—</span>}
                        </span>
                        {canWrite && (
                          <button
                            onClick={() => setEditNote({ id: p.id, note: p.deliveryNote || '' })}
                            title="Edit note"
                            style={{ padding: '2px 6px', background: 'none', border: '1px solid #E2E8F0',
                              borderRadius: 5, cursor: 'pointer', fontSize: 11, color: '#94A3B8',
                              flexShrink: 0 }}>
                            ✎
                          </button>
                        )}
                      </div>
                    </td>

                    {/* Status dropdown */}
                    <td style={{ padding: '12px 16px' }}>
                      {canWrite ? (
                        <select value={st} onChange={e => setStatus(p, e.target.value)}
                          style={{ padding: '5px 10px', borderRadius: 6, border: '1px solid #E2E8F0',
                            fontSize: 12, color: '#0F172A', cursor: 'pointer' }}>
                          {['pending', 'out', 'delivered', 'missed'].map(s => (
                            <option key={s} value={s} style={{ textTransform: 'capitalize' }}>{s}</option>
                          ))}
                        </select>
                      ) : (
                        <span style={{ color: '#94A3B8', fontSize: 12, textTransform: 'capitalize' }}>{st}</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
